/* @flow */
import * as React from 'react';
import idx from 'idx';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { OrderedMap } from 'immutable';
import Cookies from 'lib/Cookies';

import apiRequest from 'lib/apiRequest';
import assertions from 'lib/assertions';
import Button from 'components/ButtonDeprecated';
import LoadingIndicator from 'components/LoadingIndicator';
import Columns2 from 'components/Columns2';
import FormElement from 'components/FormElement';
import Glyph from 'components/Glyph';
import Image from 'components/Image';
import Input from 'components/Input';
import MediaObject from 'components/MediaObject';
import ReduxUploadButton from 'components/ReduxUploadButton';
import Select from 'components/Select';
import Textarea from 'components/Textarea';
import ToastActions from 'actions/ToastActions';
import {
  MODAL_FILE_CONTEXT_KEY,
  loadThumbnail,
  removeFile,
  uploadRequest,
} from 'reducers/fileUploads';
import { getFirstFileId, getFirstFile, uploadFile } from 'lib/fileUploadHelper';
import type { UploadRequestData } from 'reducers/fileUploads';

import './styles/BugNubForm.scss';

// for mocking in tests
export const libs = {
  apiRequest,
};

type Props = {
  onSubmit?: Function,
  asanaUsers: { id: string, name: string }[],

  /* redux props */
  fileUploads: OrderedMap<string, UploadRequestData>,
  loadThumbnail: (fileContextKey: string, filename: string, thumbnail: string) => void,
  removeFile: (fileContextKey: string, filename: string) => void,
  uploadRequest: (fileContextKey: string, filename: string, blob: File) => void,
};

type State = {
  title: ?string,
  description: ?string,
  asana_project_id?: ?string,
  asana_user_id?: ?string,
  submitting: boolean,
};

const ASANA_PROJECTS = [
  { value: '52679726735761', label: 'General bug' },
  { value: '65290911710182', label: 'Product feedback' },
  { value: '36814269195419', label: 'Bug bash' },
  { value: '215922689980189', label: 'Breakfast Club' },
  { value: '10397352269805', label: 'Sticky Fingers' },
  { value: '32507888495125', label: 'Hufflepuff' },
];

class BugNubForm extends React.Component<Props, State> {
  state: State = {
    title: '',
    description: '',
    submitting: false,
    asana_user_id: Cookies.get('asana_user_id') || '',
  };

  componentWillReceiveProps(nextProps: Props) {
    const currPropsFileUpload = getFirstFile(this.props.fileUploads);
    const nextPropsFileUpload = getFirstFile(nextProps.fileUploads);
    const errored =
      currPropsFileUpload &&
      !currPropsFileUpload.isError &&
      nextPropsFileUpload &&
      nextPropsFileUpload.isError;

    if (errored) {
      ToastActions.showErrorToast(
        'Looks like there was an error uploading that file - please try again'
      );
    }
  }

  handlePaste = (event: *) => {
    const file = event.clipboardData.items[0].getAsFile();
    if (file) {
      uploadFile(
        MODAL_FILE_CONTEXT_KEY,
        file,
        this.props.uploadRequest,
        this.props.loadThumbnail,
        false
      );
    }
  };

  handleSubmit = (data: *) => {
    const { fileUploads } = this.props;
    const firstFileId = getFirstFileId(fileUploads);
    const { title, asana_project_id, asana_user_id, description } = data;
    this.setState({ submitting: true });
    Cookies.set('asana_user_id', asana_user_id);
    libs
      .apiRequest('/bugs/report', 'POST', {
        title,
        asana_project_id,
        asana_user_id,
        file_id: idx(fileUploads.first(), _ => _.file.attributes.id) || null,
        description,
      })
      .then(res => {
        this.setState({ submitting: false });
        if (res.error) {
          ToastActions.showErrorToast(res.message);
        } else {
          if (this.props.onSubmit) {
            this.props.onSubmit();
          }
          if (firstFileId) {
            this.props.removeFile(MODAL_FILE_CONTEXT_KEY, firstFileId);
          }
          ToastActions.showSuccessToast('Your bug has been successfully submitted!');
        }
      })
      .catch(e => {
        this.setState({ submitting: false });
        ToastActions.showErrorToast('Oops! Looks like something went wrong.');
        console.error('bug nub submission failed', e);
      });
  };

  renderFileUploadButton() {
    return (
      <ReduxUploadButton
        fileContextKey={MODAL_FILE_CONTEXT_KEY}
        level="secondary"
        weight="normal"
        size="xsmall"
        color="pool"
        tooltip="Attach screenshot"
        tooltipAlignment="left"
        tooltipPlacement="top">
        <Glyph name="paperclip" size="large" solo />
      </ReduxUploadButton>
    );
  }

  renderFilePreviewOrButton() {
    const { fileUploads } = this.props;

    const firstFileId = getFirstFileId(fileUploads);
    const uploadRequestData = getFirstFile(fileUploads);
    const removeFirstFile = () =>
      firstFileId && this.props.removeFile(MODAL_FILE_CONTEXT_KEY, firstFileId);

    if (uploadRequestData) {
      if (uploadRequestData.isError) {
        return this.renderFileUploadButton();
      } else {
        const progress = uploadRequestData.progressPercentage;
        return (
          <div className="BugNubForm-screenshot">
            {progress === 100 ? (
              <div className="BugNubForm-screenshot-thumbnail" onClick={removeFirstFile}>
                <Image src={uploadRequestData.thumbnail} />
              </div>
            ) : (
              <LoadingIndicator align="center" size="xsmall" />
            )}
          </div>
        );
      }
    } else {
      return this.renderFileUploadButton();
    }
  }

  isValid() {
    return (
      this.state.title &&
      this.state.description &&
      this.state.asana_project_id &&
      this.state.asana_user_id
    );
  }

  render() {
    return (
      <div className="BugNubForm" onPaste={this.handlePaste}>
        <Columns2 gutter="xxsmall">
          <FormElement
            label="Type of bug"
            labelHidden
            validate={project => {
              assertions.notEmpty(project);
            }}
            onValidChange={project => this.setState({ asana_project_id: project })}
            onInvalidChange={() => this.setState({ asana_project_id: undefined })}
            element={Select}
            elementProps={{
              options: ASANA_PROJECTS,
              value: this.state.asana_project_id,
              placeholder: 'Type of bug',
            }}
          />
          <FormElement
            label="Who are you?"
            labelHidden
            validate={user => {
              assertions.notEmpty(user);
            }}
            onValidChange={user => this.setState({ asana_user_id: user })}
            onInvalidChange={() => this.setState({ asana_user_id: undefined })}
            element={Select}
            elementProps={{
              options: this.props.asanaUsers,
              value: this.state.asana_user_id,
              placeholder: 'Who are you?',
            }}
          />
        </Columns2>
        <MediaObject figure="right" gutter="xsmall">
          <FormElement
            label="Bug summary"
            labelHidden
            validate={e => {
              assertions.notEmpty(e.target.value);
              assertions.lengthLesserThanOrEqualTo(e.target.value, 80);
            }}
            onValidChange={e => {
              this.setState({
                title: e.target.value,
              });
            }}
            onInvalidChange={() => {
              this.setState({
                title: undefined,
              });
            }}
            element={Input}
            elementProps={{
              placeholder: 'A short description of the bug or issue',
            }}
          />
          {this.renderFilePreviewOrButton()}
        </MediaObject>
        <FormElement
          label="Steps to reproduce"
          labelHidden
          validate={e => {
            assertions.notEmpty(e.target.value);
          }}
          onValidChange={e =>
            this.setState({
              description: e.target.value,
            })
          }
          onInvalidChange={() =>
            this.setState({
              description: undefined,
            })
          }
          element={Textarea}
          elementProps={{
            placeholder:
              'Detailed instructions on to reproduce this issue (you can also paste a screenshot here to upload it)',
            value: this.state ? this.state.description : '',
          }}
        />
        <Button
          full
          onClick={() =>
            this.handleSubmit({
              title: this.state.title,
              asana_project_id: this.state.asana_project_id,
              asana_user_id: this.state.asana_user_id,
              description: this.state.description,
            })
          }
          data-test="submit-button"
          disabled={!this.isValid() || this.state.submitting}>
          Submit bug
        </Button>
      </div>
    );
  }
}

const mapStateToProps = (state: Object) => {
  const { fileUploads } = state;
  const fileGroup = fileUploads.get(MODAL_FILE_CONTEXT_KEY);
  return {
    fileUploads: fileGroup || new OrderedMap(),
  };
};

const mapDispatchToProps = (dispatch: Function) =>
  bindActionCreators(
    {
      removeFile,
      uploadRequest,
      loadThumbnail,
    },
    dispatch
  );

export { BugNubForm };

export default connect(mapStateToProps, mapDispatchToProps)(BugNubForm);
